import { Component, Input, OnChanges } from '@angular/core';

/**
 * The icons of the «Заказы» mockups (OrderDrawer / Dialogs boards) — the mockup's own line drawings, inlined, so the
 * live drawer, its dialogs and the blocks it shares with the archive show exactly the glyphs the design has (antd's
 * set has no cake / candle / truck, and its phone, pencil or card are drawn differently). `currentColor` everywhere:
 * the colour is the host's.
 */
export type OrderLiveIconName =
  | 'edit' | 'close' | 'copy' | 'external' | 'card' | 'phone' | 'gift' | 'play' | 'pause' | 'cake' | 'camera' | 'candle'
  | 'truck' | 'sparkles' | 'pencil' | 'check' | 'message' | 'clock' | 'lock' | 'lock-plain' | 'alert' | 'file' | 'refresh' | 'ban'
  // 1c — the carts' board (CartDrawer): continue ➝, prolong (a clock face), the cart, «Позвонить», the plain tick
  | 'arrow' | 'timer' | 'cart' | 'call' | 'tick';

interface IconDef {
  d: string;
  /** Stroke width of a line icon; a solid icon (`fill`) has none. */
  width: number;
  fill?: boolean;
}

const ICONS: Record<OrderLiveIconName, IconDef> = {
  edit: { d: 'M4 20h4l10-10-4-4L4 16zM13.5 6.5l4 4', width: 2 },
  close: { d: 'M6 6l12 12M18 6L6 18', width: 2 },
  copy: { d: 'M9 9h11v11H9zM5 15H4V4h11v1', width: 1.8 },
  external: { d: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5', width: 1.8 },
  card: { d: 'M3 7h18v11H3zM3 11h18M7 15h3', width: 1.8 },
  phone: { d: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2', width: 1.8 },
  gift: { d: 'M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7c-1.5-3-5-3.5-5-1s3 1 5 1M12 7c1.5-3 5-3.5 5-1s-3 1-5 1', width: 1.8 },
  play: { d: 'M8 5l11 7-11 7z', width: 0, fill: true },
  pause: { d: 'M7 5h4v14H7zM13 5h4v14h-4z', width: 0, fill: true },
  cake: { d: 'M4 20h16M5 20v-7h14v7M5 16c2 1.5 3 1.5 4.7 0 1.6 1.5 3 1.5 4.6 0 1.7 1.5 2.7 1.5 4.7 0M12 13V9M12 6v.01', width: 1.8 },
  camera: { d: 'M4 8h3l2-3h6l2 3h3v11H4zM9 13.5a3 3 0 1 0 6 0a3 3 0 1 0-6 0', width: 1.8 },
  candle: { d: 'M9 21V10h6v11M12 10V7M12 3c1.2 1.3 1.2 2.7 0 4-1.2-1.3-1.2-2.7 0-4', width: 1.8 },
  truck: { d: 'M3 7h11v9H3zM14 10h4l3 3v3h-7M5.5 17.5a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0M15.5 17.5a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0', width: 1.8 },
  sparkles: { d: 'M10 4l1.7 4.3L16 10l-4.3 1.7L10 16l-1.7-4.3L4 10l4.3-1.7zM18 14l.9 2.1L21 17l-2.1.9L18 20l-.9-2.1L15 17l2.1-.9z', width: 1.8 },
  pencil: { d: 'M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4', width: 1.8 },
  check: { d: 'M5 12l5 5 9-10', width: 1.8 },
  message: { d: 'M4 5h16v11H9l-5 4z', width: 1.8 },
  clock: { d: 'M12 7v5l3 2M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0', width: 1.9 },
  lock: { d: 'M6 11V8a6 6 0 0 1 12 0v3M5 11h14v10H5zM12 15v2', width: 1.9 },
  'lock-plain': { d: 'M6 11V8a6 6 0 0 1 12 0v3M5 11h14v10H5z', width: 2 },
  alert: { d: 'M12 8v5M12 16h.01M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0', width: 1.9 },
  file: { d: 'M6 3h8l4 4v14H6zM14 3v4h4M9 13h6M9 17h4', width: 1.6 },
  refresh: { d: 'M4 12a8 8 0 0 1 14-5.3L20 9M20 4v5h-5M20 12a8 8 0 0 1-14 5.3L4 15M4 20v-5h5', width: 2 },
  ban: { d: 'M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0M5.6 5.6l12.8 12.8', width: 1.8 },
  arrow: { d: 'M5 12h14M13 6l6 6-6 6', width: 2 },
  timer: { d: 'M3.5 12a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0-17 0M12 7.5V12l3 2', width: 2 },
  cart: { d: 'M4 5h2l2.2 10h9.6L20 8H7.2M8.2 19a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0M15.7 19a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0', width: 1.8 },
  call: { d: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1z', width: 2 },
  tick: { d: 'M5 12.5l4.5 4.5L19 7.5', width: 1.8 },
};

@Component({
  selector: 'app-order-live-icon',
  template: `<svg [attr.width]="size" [attr.height]="size" viewBox="0 0 24 24" [attr.fill]="fill" [attr.stroke]="stroke" [attr.stroke-width]="width"
                  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path [attr.d]="path"></path></svg>`,
  styles: [':host { display: inline-flex; flex-shrink: 0; }'],
})
export class OrderLiveIconComponent implements OnChanges {
  @Input() name: OrderLiveIconName = 'close';
  /** Side of the square, px. */
  @Input() size = 16;
  /** Overrides the icon's own stroke width (the same drawing is thinner on a large tile). */
  @Input() strokeWidth?: number;

  path = ICONS.close.d;
  fill = 'none';
  stroke: string | null = 'currentColor';
  width: number | null = ICONS.close.width;

  ngOnChanges(): void {
    const def = ICONS[this.name] ?? ICONS.close;
    this.path = def.d;
    this.fill = def.fill ? 'currentColor' : 'none';
    this.stroke = def.fill ? null : 'currentColor';
    this.width = def.fill ? null : (this.strokeWidth ?? def.width);
  }
}
